import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
    DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QRCodeSVG } from "qrcode.react";
import {
    ArrowLeft, Phone, Siren, ShieldCheck, Landmark, Scroll, Home,
    Users, FileText, BadgeAlert, Share2, Copy, Trash2, Lock,
} from "lucide-react";
import { toast } from "sonner";

export default function Crisis() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [shares, setShares] = useState([]);
    const [latestShare, setLatestShare] = useState(null);
    const [label, setLabel] = useState("Family");
    const [days, setDays] = useState(7);
    const [creating, setCreating] = useState(false);

    const loadShares = async () => {
        try {
            const { data } = await api.get("/crisis/share");
            setShares(data);
        } catch (_e) {}
    };

    useEffect(() => {
        api.get("/crisis").then(({ data }) => setData(data));
        loadShares();
    }, []);

    const create = async (e) => {
        e?.preventDefault?.();
        setCreating(true);
        try {
            const { data } = await api.post("/crisis/share", { label, expires_in_days: Number(days) });
            setLatestShare(data);
            await loadShares();
            toast.success("Emergency share link created");
        } catch (e2) {
            toast.error(e2.response?.data?.detail || "Failed to create share");
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (id) => {
        if (!window.confirm("Revoke this emergency link?")) return;
        await api.delete(`/crisis/share/${id}`);
        toast.success("Revoked");
        loadShares();
    };

    const shareUrl = latestShare ? `${window.location.origin}/emergency/${latestShare.token}` : "";
    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied");
    };

    return (
        <div className="crisis-mode min-h-screen bg-background text-foreground" data-testid="crisis-page">
            {/* Crisis header */}
            <header className="border-b border-border">
                <div className="px-8 py-6 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        onClick={() => nav(-1)}
                        data-testid="crisis-exit"
                        className="text-foreground/80 hover:text-foreground hover:bg-secondary"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Exit Crisis Mode
                    </Button>
                    <div className="flex items-center gap-3">
                        <Dialog open={shareOpen} onOpenChange={(o) => { setShareOpen(o); if (!o) setLatestShare(null); }}>
                            <DialogTrigger asChild>
                                <Button data-testid="crisis-share-btn" className="rounded-full">
                                    <Share2 className="mr-2 h-4 w-4" /> Share emergency access
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg" data-testid="crisis-share-dialog">
                                <DialogHeader>
                                    <DialogTitle>Share emergency access</DialogTitle>
                                    <DialogDescription>
                                        Generate a read-only link with a 4-digit PIN. Share the link & PIN <em>via separate channels</em> with the family member who should have access.
                                    </DialogDescription>
                                </DialogHeader>
                                {!latestShare ? (
                                    <form onSubmit={create} className="space-y-4">
                                        <div>
                                            <Label htmlFor="lbl">Label</Label>
                                            <Input id="lbl" value={label} onChange={(e) => setLabel(e.target.value)}
                                                   data-testid="share-label" placeholder="Spouse / Brother / Lawyer…" />
                                        </div>
                                        <div>
                                            <Label htmlFor="days">Valid for (days)</Label>
                                            <Input id="days" type="number" min={1} max={90} value={days}
                                                   onChange={(e) => setDays(e.target.value)}
                                                   data-testid="share-days" />
                                        </div>
                                        <DialogFooter>
                                            <Button type="submit" disabled={creating} data-testid="share-create" className="rounded-full">
                                                <Share2 className="mr-2 h-4 w-4" /> {creating ? "Creating…" : "Create link"}
                                            </Button>
                                        </DialogFooter>
                                    </form>
                                ) : (
                                    <div className="space-y-4" data-testid="share-result">
                                        <div className="rounded-lg border border-primary bg-primary/5 p-5 text-center">
                                            <QRCodeSVG value={shareUrl} size={170} level="M"
                                                       bgColor="transparent" fgColor="hsl(var(--foreground))"
                                                       className="mx-auto" />
                                            <p className="mt-3 text-xs text-muted-foreground">Scan or copy the link</p>
                                        </div>
                                        <div>
                                            <Label>Link</Label>
                                            <div className="flex items-center gap-2">
                                                <Input readOnly value={shareUrl} data-testid="share-url" className="font-mono text-xs" />
                                                <Button type="button" variant="outline" size="sm" onClick={() => copy(shareUrl)} data-testid="share-copy-url">
                                                    <Copy className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> 4-digit PIN — share separately</Label>
                                            <div className="flex items-center gap-2">
                                                <Input readOnly value={latestShare.pin} data-testid="share-pin" className="font-mono text-2xl tracking-[0.4em] text-center" />
                                                <Button type="button" variant="outline" size="sm" onClick={() => copy(latestShare.pin)} data-testid="share-copy-pin">
                                                    <Copy className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                The PIN is shown only once. Store or share it now.
                                            </p>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Valid until {latestShare.expires_at?.slice(0, 10)}.
                                            You can revoke the link any time below.
                                        </p>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>
                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-primary">
                            <Siren className="h-4 w-4" /> CRISIS MODE — read only
                        </div>
                    </div>
                </div>
            </header>

            {!data ? <LoadingBlock label="Opening emergency view" /> : (
                <div className="px-8 py-10 space-y-10 max-w-6xl mx-auto">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">
                            Owner of this vault
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                            {data.owner?.name || "—"}
                        </h1>
                        <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                            {data.owner?.phone ? <Item icon={Phone} label="Phone" value={data.owner.phone} /> : null}
                            {data.owner?.blood_group ? <Item icon={BadgeAlert} label="Blood" value={data.owner.blood_group} /> : null}
                            {data.owner?.address ? <Item icon={Home} label="Address" value={data.owner.address} /> : null}
                        </div>
                    </div>

                    {shares.filter((s) => !s.revoked).length > 0 ? (
                        <Section title="Active emergency shares" icon={Share2}>
                            <ul className="rounded-lg border border-border bg-card divide-y divide-border" data-testid="active-shares">
                                {shares.filter((s) => !s.revoked).map((s) => (
                                    <li key={s.id} className="px-5 py-3 flex items-center justify-between" data-testid={`share-row-${s.id}`}>
                                        <div>
                                            <div className="text-sm font-semibold">{s.label}</div>
                                            <div className="text-xs text-muted-foreground font-mono">
                                                expires {s.expires_at?.slice(0, 10)} · accessed {s.access_count || 0}×
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={() => revoke(s.id)} data-testid={`share-revoke-${s.id}`}>
                                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    ) : null}

                    <Section title="Trusted professionals" icon={Users}>
                        <Pros pros={data.professionals || {}} />
                    </Section>

                    <Section title="Emergency contacts" icon={Phone}>
                        {(data.emergency_contacts || []).length === 0 ? (
                            <Empty msg="No emergency contacts on file." />
                        ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.emergency_contacts.map((c, i) => (
                                    <Card key={i} testid={`crisis-contact-${i}`}>
                                        <div className="font-display text-lg font-bold">{c.name}</div>
                                        <div className="text-xs text-muted-foreground capitalize">{c.relation}</div>
                                        <a href={`tel:${c.phone}`} className="mt-3 block font-mono text-primary text-base">{c.phone}</a>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Insurance policies" icon={ShieldCheck}>
                        {(data.policies || []).length === 0 ? (
                            <Empty msg="No insurance policies on file." />
                        ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.policies.map((p) => (
                                    <Card key={p.id} testid={`crisis-policy-${p.id}`}>
                                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{p.policy_type}</div>
                                        <div className="font-display text-lg font-bold mt-1">{p.provider}</div>
                                        <div className="font-mono text-xs text-muted-foreground">#{p.policy_number}</div>
                                        <div className="mt-3 text-sm">
                                            Sum: <span className="font-mono">₹{Number(p.sum_assured || 0).toLocaleString("en-IN")}</span>
                                        </div>
                                        <div className="text-sm">
                                            Nominee: <span className="font-semibold">{p.nominee || "—"}</span>
                                            {p.nominee_relation ? <span className="text-muted-foreground"> ({p.nominee_relation})</span> : null}
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Bank accounts & FDs" icon={Landmark}>
                        {(data.accounts || []).length === 0 ? <Empty msg="No accounts on file." /> : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.accounts.map((a) => (
                                    <Card key={a.id} testid={`crisis-account-${a.id}`}>
                                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{a.account_type.replace("_", " ")}</div>
                                        <div className="font-display text-lg font-bold mt-1">{a.bank_name}</div>
                                        {a.last4 ? <div className="font-mono text-xs text-muted-foreground">**** {a.last4}</div> : null}
                                        {a.nominee ? <div className="mt-2 text-sm">Nominee: <span className="font-semibold">{a.nominee}</span></div> : null}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Legal & legacy" icon={Scroll}>
                        {(data.legal || []).length === 0 ? <Empty msg="No legal docs on file." /> : (
                            <div className="grid sm:grid-cols-2 gap-4">
                                {data.legal.map((l) => (
                                    <Card key={l.id} testid={`crisis-legal-${l.id}`}>
                                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{(l.legal_type || "").replace("_"," ")}</div>
                                        <div className="font-display text-lg font-bold mt-1">{l.title}</div>
                                        {l.executor ? <div className="text-sm mt-2">Executor: <span className="font-semibold">{l.executor}</span></div> : null}
                                        {l.location ? <div className="text-sm">Located at: <span className="font-mono">{l.location}</span></div> : null}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Assets" icon={Home}>
                        {(data.assets || []).length === 0 ? <Empty msg="No assets on file." /> : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.assets.map((a) => (
                                    <Card key={a.id} testid={`crisis-asset-${a.id}`}>
                                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{a.asset_type}</div>
                                        <div className="font-display text-lg font-bold mt-1">{a.name}</div>
                                        {a.location ? <div className="text-sm text-muted-foreground mt-2">{a.location}</div> : null}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Key documents" icon={FileText}>
                        {(data.key_documents || []).length === 0 ? <Empty msg="No key documents tagged yet." /> : (
                            <ul className="rounded-lg border border-border divide-y divide-border bg-card">
                                {data.key_documents.map((d) => (
                                    <li key={d.id} className="px-5 py-3 flex items-center justify-between" data-testid={`crisis-doc-${d.id}`}>
                                        <span className="text-sm font-semibold">{d.name}</span>
                                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{d.category}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    <p className="text-xs text-muted-foreground mt-4">
                        This screen is generated only from data the vault owner has filled in. Always verify with the
                        original documents before any claim or transaction.
                    </p>
                </div>
            )}
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section>
            <div className="flex items-center gap-3 mb-4">
                <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
            </div>
            {children}
        </section>
    );
}
function Card({ children, testid }) {
    return (
        <div className="rounded-lg border border-border bg-card p-5" data-testid={testid}>{children}</div>
    );
}
function Empty({ msg }) {
    return <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">{msg}</div>;
}
function Item({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <span className="text-muted-foreground">{label}:</span>
            <span className="font-semibold">{value}</span>
        </div>
    );
}
function Pros({ pros }) {
    const items = [
        ["Advisor", pros.advisor_name, pros.advisor_phone],
        ["CA", pros.ca_name, pros.ca_phone],
        ["Doctor", pros.doctor_name, pros.doctor_phone],
        ["Broker / Lawyer", pros.broker_name, pros.broker_phone],
    ].filter(([, n]) => n);
    if (!items.length) return <Empty msg="No professionals listed yet." />;
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map(([role, name, phone], i) => (
                <Card key={i} testid={`crisis-pro-${role}`}>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{role}</div>
                    <div className="font-display text-lg font-bold mt-1">{name}</div>
                    {phone ? <a href={`tel:${phone}`} className="font-mono text-primary text-sm">{phone}</a> : null}
                </Card>
            ))}
        </div>
    );
}
